import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api from "../../utils/api";

const API = `/api`;

const SERVICE_TYPES = ["Full Respray", "Custom Paint Job", "Colour Matching", "Dent & Scratch Repair", "Clear Coat Restoration", "Ceramic Coating", "Detailing", "Other"];

function AddEntryModal({ type, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", vehicle_model: "", service_type: "Full Respray",
    description: "", preferred_date: "", preferred_time: "11:00 AM",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.vehicle_model) {
      toast.error("Name, phone and vehicle are required");
      return;
    }
    if (type === "appointment" && !form.preferred_date) {
      toast.error("Date is required for appointments");
      return;
    }
    setSaving(true);
    try {
      const payload = type === "quote"
        ? { name: form.name, phone: form.phone, email: form.email, vehicle_model: form.vehicle_model, service_type: form.service_type, description: form.description }
        : (() => {
            const d = new Date(form.preferred_date + "T12:00:00");
            const formatted = d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
            return { name: form.name, phone: form.phone, email: form.email, vehicle_model: form.vehicle_model, service_type: form.service_type, preferred_date: formatted, preferred_time: form.preferred_time, message: form.description };
          })();
      await api.post(`${API}/admin/${type}s`, payload);
      toast.success(`${type === "quote" ? "Quote" : "Appointment"} added`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/10 text-white font-body text-sm py-2 px-0 outline-none focus:border-[#3b82f6]/50 placeholder:text-zinc-700 transition-colors";
  const labelCls = "block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-2";

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-entry-modal">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <h3 className="font-heading text-lg uppercase tracking-wide text-white">
            Add {type === "quote" ? "Quote" : "Appointment"}
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors bg-transparent border-none cursor-pointer" data-testid="modal-close-btn">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Customer name" className={inputCls} data-testid="admin-input-name" />
            </div>
            <div>
              <label className={labelCls}>Phone *</label>
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="04XX XXX XXX" className={inputCls} data-testid="admin-input-phone" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Optional" className={inputCls} data-testid="admin-input-email" />
          </div>
          <div>
            <label className={labelCls}>Vehicle *</label>
            <input value={form.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} placeholder="e.g. Nissan Silvia S15" className={inputCls} data-testid="admin-input-vehicle" />
          </div>
          <div>
            <label className={labelCls}>Service</label>
            <div className="flex flex-wrap gap-2">
              {SERVICE_TYPES.map((s) => (
                <button key={s} type="button" onClick={() => set("service_type", s)}
                  className={`px-3 py-1.5 text-xs tracking-[0.05em] uppercase font-body border cursor-pointer transition-colors ${
                    form.service_type === s ? "bg-[#3b82f6]/20 text-[#3b82f6] border-[#3b82f6]/30" : "bg-transparent border-white/10 text-zinc-600 hover:text-zinc-400"
                  }`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          {type === "appointment" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Date *</label>
                <input type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className={inputCls + " [color-scheme:dark]"} data-testid="admin-input-date" />
              </div>
              <div>
                <label className={labelCls}>Time</label>
                <select value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} className={inputCls + " bg-[#0a0a0a] [color-scheme:dark]"} data-testid="admin-input-time">
                  {["11:00 AM","11:30 AM","12:00 PM","12:30 PM","1:00 PM","1:30 PM","2:00 PM","2:30 PM","3:00 PM","3:30 PM","4:00 PM","4:30 PM"].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Any details..." rows={3} className={inputCls + " resize-none"} data-testid="admin-input-notes" />
          </div>
          <button type="submit" disabled={saving}
            data-testid="admin-submit-btn"
            className="w-full py-3 font-heading text-sm tracking-[0.15em] uppercase bg-white text-black hover:bg-zinc-200 disabled:opacity-50 transition-colors cursor-pointer border-none">
            {saving ? "Saving..." : `Add ${type === "quote" ? "Quote" : "Appointment"}`}
          </button>
        </form>
      </div>
    </div>
  );
}

export { AddEntryModal };
